import React, { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, Plus, Pencil, EyeOff, Eye, X, Save, Dumbbell, Search, Download, Upload, CheckCircle, AlertCircle, Sparkles, Trash2, Play, VideoOff } from "lucide-react";
import { ejerciciosApi, machinesApi, accesoriosApi, formatApiError, tokenStore, API_BASE } from "../../lib/api";

const GRUPOS = [
  // Pierna (específicos)
  "Femoral", "Quadriceps", "Glúteo", "Gemelo", "aductor", "abductor", "Lumbar",
  // Tren superior
  "espalda", "pecho", "Hombro", "Biceps", "Triceps", "brazos",
  // Global
  "core", "cardio", "Full body", "posterior_cadena", "accesorio",
];

// Parse comma-separated grupo_muscular — first is primary
function parseGrupos(raw) {
  if (!raw) return { primary: null, secondary: [] };
  const parts = raw.split(",").map(s => s.trim()).filter(Boolean);
  return { primary: parts[0] || null, secondary: parts.slice(1) };
}
const ESFUERZOS = ["Empuje", "Tracción", "Pierna", "Cardio", "Core", "Isométrico", "N/A"];
const DIFS = [{ val: null, label: "No especificada" }, { val: 1, label: "1 · Fácil" }, { val: 2, label: "2 · Medio" }, { val: 3, label: "3 · Avanzado" }];
const EQUIPOS = [
  { val: "maquina",  label: "Máquina" },
  { val: "material", label: "Material (mancuernas, KB…)" },
  { val: "libre",    label: "Peso propio / sin equipo" },
];
const EMPTY_FORM = {
  nombre: "", codigo: "", numero_maquina: "", grupo_muscular: "", tipo_esfuerzo: "",
  dificultad: null, equipo_necesario: "libre", video_url: "", instrucciones_breves: "",
  accesorios: [],
};

const INPUT = "w-full bg-[#111] border border-white/12 text-white px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] transition-colors placeholder-white/20";
const LABEL = "block text-[10px] uppercase tracking-widest text-white/40 font-bold mb-1";

export default function EjerciciosTab() {
  const [exercises, setExercises] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [search, setSearch] = useState("");
  const [filterGrupo, setFilterGrupo] = useState("");
  const [filterEquipo, setFilterEquipo] = useState("");
  const [filterActiva, setFilterActiva] = useState("todas");
  const [filterVideo, setFilterVideo] = useState("todos"); // "todos" | "con" | "sin"
  const [err, setErr] = useState("");
  // Import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  // AI generation state
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiState, setAiState] = useState(null);
  const [aiPolling, setAiPolling] = useState(false);
  // Delete all state
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deletingAll, setDeletingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [exs, ms] = await Promise.all([ejerciciosApi.adminList(), machinesApi.adminList()]);
      setExercises(exs);
      setMachines(ms);
    } catch { setErr("Error cargando ejercicios"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleNew = () => { setIsNew(true); setEditing({ ...EMPTY_FORM }); };
  const handleEdit = (ex) => {
    setIsNew(false);
    setEditing({
      ...EMPTY_FORM, ...ex,
      dificultad: ex.dificultad ?? null,
      numero_maquina: ex.numero_maquina ?? "",
    });
  };
  const handleClose = () => { setEditing(null); };

  const handleToggle = async (ex) => {
    try {
      if (ex.activa) { await ejerciciosApi.deactivate(ex.id); }
      else { await ejerciciosApi.update(ex.id, { activa: true }); }
      setExercises((prev) => prev.map((e) => e.id === ex.id ? { ...e, activa: !ex.activa } : e));
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Error"); }
  };

  const handleDelete = async (ex) => {
    if (!window.confirm(`¿Eliminar permanentemente "${ex.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await ejerciciosApi.hardDelete(ex.id);
      setExercises((prev) => prev.filter((e) => e.id !== ex.id));
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Error al eliminar"); }
  };

  const handleSaved = async () => { setEditing(null); await load(); };

  const handleDeleteAll = async () => {
    if (deleteConfirm !== "BORRAR") return;
    setDeletingAll(true);
    try {
      await ejerciciosApi.deleteAll();
      setShowDeleteAll(false);
      setDeleteConfirm("");
      await load();
    } catch { setErr("Error al borrar todos los ejercicios"); }
    finally { setDeletingAll(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/ejercicios/plantilla-excel`, {
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
      });
      if (!res.ok) throw new Error("Error descargando plantilla");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "plantilla_ejercicios_xpro.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Error descargando la plantilla"); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API_BASE}/admin/ejercicios/importar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
        body: form,
      });
      const d = await res.json();
      if (!res.ok) { alert(d.detail || "Error en importación"); return; }
      setImportResult(d);
      await load();
    } catch { alert("Error importando archivo"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  // AI generation
  const pollAiState = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/ejercicios/generar-ia/estado`, {
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
      });
      const d = await res.json();
      setAiState(d);
      if (d.estado === "en_progreso") {
        setTimeout(pollAiState, 2500);
      } else if (d.estado === "completado") {
        setAiPolling(false);
        await load();
      }
    } catch { /* ignore */ }
  }, []);

  const handleLaunchAi = async () => {
    setShowAiModal(false);
    setAiPolling(true);
    try {
      const res = await fetch(`${API_BASE}/admin/ejercicios/generar-ia`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
      });
      if (!res.ok) {
        const d = await res.json();
        alert(d.detail || "Error iniciando generación");
        setAiPolling(false);
        return;
      }
      pollAiState();
    } catch { setAiPolling(false); alert("Error de conexión"); }
  };

  // Filtered list
  const filtered = exercises.filter((ex) => {
    if (filterActiva === "activas" && !ex.activa) return false;
    if (filterActiva === "inactivas" && ex.activa) return false;
    if (filterGrupo && !(ex.grupo_muscular || "").toLowerCase().includes(filterGrupo.toLowerCase())) return false;
    if (filterEquipo && ex.equipo_necesario !== filterEquipo) return false;
    if (filterVideo === "con" && !ex.video_url) return false;
    if (filterVideo === "sin" && ex.video_url) return false;
    if (search && !ex.nombre.toLowerCase().includes(search.toLowerCase()) && !ex.codigo.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const equipoLabel = (v) => EQUIPOS.find((e) => e.val === v)?.label ?? v;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="ejercicios-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-black tracking-tight">Ejercicios</h1>
          <p className="text-white/35 text-xs mt-0.5">{exercises.filter((e) => e.activa).length} ejercicios activos</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowAiModal(true)} data-testid="generar-ejercicios-ia-btn"
            disabled={aiPolling}
            className="flex items-center gap-2 border border-[#F2C230]/35 text-[#F2C230] font-bold text-[10px] tracking-[0.2em] uppercase px-3 py-2.5 hover:bg-[#F2C230]/10 transition-colors disabled:opacity-40">
            {aiPolling ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {aiPolling ? "Generando…" : "Generar con IA"}
          </button>
          <button onClick={handleDownloadTemplate} data-testid="download-template-btn"
            title="Descargar plantilla Excel"
            className="flex items-center gap-2 border border-white/15 text-white/50 font-bold text-[10px] tracking-[0.2em] uppercase px-3 py-2.5 hover:border-[#FFCC00]/50 hover:text-[#FFCC00] transition-colors">
            <Download size={13} /> Plantilla Excel
          </button>
          <label data-testid="import-exercises-btn" title="Importar desde Excel o CSV"
            className="flex items-center gap-2 border border-white/15 text-white/50 font-bold text-[10px] tracking-[0.2em] uppercase px-3 py-2.5 hover:border-[#FFCC00]/50 hover:text-[#FFCC00] transition-colors cursor-pointer">
            {importing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {importing ? "Importando…" : "Importar Excel"}
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportFile} disabled={importing} />
          </label>
          <button onClick={handleNew} data-testid="new-exercise-btn"
            className="flex items-center gap-2 bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 hover:bg-white transition-colors">
            <Plus size={13} /> Nuevo ejercicio
          </button>
          <button onClick={() => { setShowDeleteAll(true); setDeleteConfirm(""); }} data-testid="delete-all-exercises-btn"
            className="flex items-center gap-2 border border-red-500/40 text-red-400 font-black text-[10px] tracking-[0.2em] uppercase px-3 py-2.5 hover:bg-red-500/10 transition-colors">
            <Trash2 size={13} /> Borrar todo
          </button>
        </div>
      </div>

      {/* Import result banner */}
      {importResult && (
        <div className="mb-4 flex items-center gap-3 border border-green-500/25 bg-green-500/8 px-4 py-3" data-testid="import-result">
          <CheckCircle size={15} className="text-green-400 shrink-0" />
          <span className="text-sm text-green-300">
            Importación completada — <strong>{importResult.inserted}</strong> nuevos · <strong>{importResult.updated}</strong> actualizados · {importResult.skipped} omitidos
          </span>
          <button onClick={() => setImportResult(null)} className="ml-auto text-white/30 hover:text-white"><X size={14} /></button>
        </div>
      )}

      {/* AI progress bar */}
      {(aiPolling || (aiState && aiState.estado === "completado")) && aiState && (
        <div className="mb-4 border border-white/10 bg-[#0a0a0a] p-4" data-testid="ai-ex-progress">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-black tracking-[0.2em] uppercase text-[#F2C230]">
              {aiState.estado === "en_progreso" ? "Generando ejercicios con IA…" : "Generación completada"}
            </p>
            <span className="text-[10px] text-white/40">{aiState.completadas}/{aiState.total}</span>
          </div>
          <div className="w-full bg-white/8 h-1.5 overflow-hidden">
            <div className="h-full bg-[#F2C230] transition-all duration-500"
              style={{ width: `${Math.round(((aiState.completadas || 0) / (aiState.total || 1)) * 100)}%` }} />
          </div>
          {aiState.estado === "completado" && (
            <div className="flex items-center gap-2 mt-2">
              <CheckCircle size={12} className="text-green-400" />
              <span className="text-xs text-green-300">
                {aiState.nuevos_ejercicios} ejercicios nuevos creados en {aiState.completadas} lotes.
                {aiState.fallidas > 0 && <span className="text-amber-400 ml-1">{aiState.fallidas} fallidos.</span>}
              </span>
            </div>
          )}
        </div>
      )}

      {/* AI confirmation modal */}
      {showAiModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ai-ex-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowAiModal(false)} />
          <div className="relative w-full max-w-2xl bg-[#0d0d0d] border border-white/10 shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#F2C230]/10 border border-[#F2C230]/30 flex items-center justify-center">
                <Sparkles size={18} className="text-[#F2C230]" />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#F2C230] font-black">Generador IA</p>
                <p className="text-sm font-bold text-white">Ejercicios para todas las máquinas</p>
              </div>
            </div>
            <div className="space-y-2 mb-6 text-xs text-white/55 leading-relaxed">
              <p>La IA generará ejercicios para <strong className="text-white">cada máquina activa</strong> del gimnasio y para los <strong className="text-white">3 equipos funcionales</strong> (kettlebell, saco, peso propio):</p>
              <ul className="list-disc pl-4 space-y-1 mt-2">
                <li>Máquinas estándar: <strong className="text-white/70">4–6</strong> ejercicios cada una</li>
                <li>Multiestaciones y poleas: <strong className="text-white/70">10–15</strong> ejercicios (todos los agarres)</li>
                <li>Funcional: <strong className="text-white/70">6–10</strong> ejercicios por categoría</li>
              </ul>
              <div className="border border-amber-500/25 bg-amber-500/8 px-3 py-2 text-amber-200/70 mt-3">
                Todos los ejercicios se crearán como <strong>inactivos</strong>. Los activarás tú uno a uno al añadir su vídeo tutorial.
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={handleLaunchAi} data-testid="confirm-ai-ex-gen"
                className="flex-1 flex items-center justify-center gap-2 bg-[#F2C230] text-black font-black text-[11px] tracking-[0.2em] uppercase py-3 hover:bg-white transition-colors">
                <Sparkles size={13} /> Generar ejercicios
              </button>
              <button onClick={() => setShowAiModal(false)}
                className="px-5 border border-white/12 text-white/50 text-[11px] font-bold tracking-wider uppercase hover:border-white/30 hover:text-white transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar…"
            data-testid="search-exercises"
            className="bg-[#111] border border-white/10 text-white text-xs pl-8 pr-3 py-2 outline-none focus:border-[#FFCC00] w-48 transition-colors" />
        </div>
        <select value={filterGrupo} onChange={(e) => setFilterGrupo(e.target.value)} data-testid="filter-grupo"
          className="bg-[#111] border border-white/10 text-white text-xs px-3 py-2 outline-none focus:border-[#FFCC00] transition-colors appearance-none">
          <option value="">Todos los grupos</option>
          {GRUPOS.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={filterEquipo} onChange={(e) => setFilterEquipo(e.target.value)} data-testid="filter-equipo"
          className="bg-[#111] border border-white/10 text-white text-xs px-3 py-2 outline-none focus:border-[#FFCC00] transition-colors appearance-none">
          <option value="">Todos los equipos</option>
          {EQUIPOS.map((e) => <option key={e.val} value={e.val}>{e.label}</option>)}
        </select>
        <select value={filterActiva} onChange={(e) => setFilterActiva(e.target.value)} data-testid="filter-activa"
          className="bg-[#111] border border-white/10 text-white text-xs px-3 py-2 outline-none focus:border-[#FFCC00] transition-colors appearance-none">
          <option value="activas">Solo activos</option>
          <option value="inactivas">Solo inactivos</option>
          <option value="todos">Todos</option>
        </select>
        <select value={filterVideo} onChange={(e) => setFilterVideo(e.target.value)} data-testid="filter-video"
          className="bg-[#111] border border-white/10 text-white text-xs px-3 py-2 outline-none focus:border-[#FFCC00] transition-colors appearance-none">
          <option value="todos">Todos · vídeo</option>
          <option value="con">Con vídeo</option>
          <option value="sin">Sin vídeo</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-3 text-white/30 py-12 justify-center">
          <Loader2 size={20} className="animate-spin" /> Cargando ejercicios…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-white/20">
          <Dumbbell size={36} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm">No hay ejercicios que coincidan</p>
        </div>
      ) : (
        <div className="border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 bg-white/3">
                <th className="text-left px-4 py-3 text-[9px] font-black tracking-[0.2em] uppercase text-white/40">Código</th>
                <th className="text-left px-4 py-3 text-[9px] font-black tracking-[0.2em] uppercase text-white/40">Nombre</th>
                <th className="text-left px-4 py-3 text-[9px] font-black tracking-[0.2em] uppercase text-white/40 hidden md:table-cell">Grupo</th>
                <th className="text-left px-4 py-3 text-[9px] font-black tracking-[0.2em] uppercase text-white/40 hidden lg:table-cell">Equipo</th>
                <th className="text-left px-4 py-3 text-[9px] font-black tracking-[0.2em] uppercase text-white/40 hidden lg:table-cell">Máquina</th>
                <th className="px-4 py-3 text-[9px] font-black tracking-[0.2em] uppercase text-white/40 text-center w-16">Vídeo</th>
                <th className="text-left px-4 py-3 text-[9px] font-black tracking-[0.2em] uppercase text-white/40">Estado</th>
                <th className="px-4 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((ex) => (
                <tr key={ex.id} className="hover:bg-white/2 transition-colors" data-testid={`ex-row-${ex.codigo}`}>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-black tracking-widest text-[#FFCC00]/70">{ex.codigo}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-bold text-sm text-white">{ex.nombre}</p>
                    {ex.dificultad && <p className="text-[10px] text-white/35 mt-0.5">Dif. {ex.dificultad}</p>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {(() => {
                      const { primary, secondary } = parseGrupos(ex.grupo_muscular);
                      return (
                        <div className="flex items-center gap-1 flex-wrap">
                          {primary && <span className="text-[10px] font-black px-1.5 py-0.5 bg-[#F2C230]/15 text-[#F2C230] border border-[#F2C230]/25">{primary}</span>}
                          {secondary.map(g => <span key={g} className="text-[10px] text-white/35">{g}</span>)}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className={`text-[9px] font-black tracking-wider uppercase px-2 py-1 border ${
                      ex.equipo_necesario === "maquina" ? "border-blue-500/30 text-blue-400" :
                      ex.equipo_necesario === "material" ? "border-orange-500/30 text-orange-400" :
                      "border-green-500/30 text-green-400"
                    }`}>
                      {ex.equipo_necesario === "maquina" ? "Máquina" : ex.equipo_necesario === "material" ? "Material" : "Libre"}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="text-xs text-white/40">{ex.numero_maquina ? `#${ex.numero_maquina}` : "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-center" data-testid={`video-indicator-${ex.id}`}>
                    {ex.video_url
                      ? <span title={ex.video_url} className="inline-flex items-center justify-center w-6 h-6 bg-green-500/12 text-green-400 rounded"><Play size={11} fill="currentColor" /></span>
                      : <span title="Sin vídeo" className="inline-flex items-center justify-center w-6 h-6 bg-white/4 text-white/20 rounded"><VideoOff size={11} /></span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[9px] font-black tracking-wider uppercase px-2 py-1 border ${
                      ex.activa ? "border-[#FFCC00]/30 text-[#FFCC00]/70" : "border-white/10 text-white/25"
                    }`}>
                      {ex.activa ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleEdit(ex)} data-testid={`edit-ex-${ex.id}`}
                        className="p-1.5 text-white/30 hover:text-[#FFCC00] transition-colors" title="Editar">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => handleToggle(ex)} data-testid={`toggle-ex-${ex.id}`}
                        className="p-1.5 text-white/30 hover:text-white transition-colors" title={ex.activa ? "Desactivar" : "Activar"}>
                        {ex.activa ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button onClick={() => handleDelete(ex)} data-testid={`delete-ex-${ex.id}`}
                        className="p-1.5 text-white/30 hover:text-red-400 transition-colors" title="Eliminar permanentemente">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {editing && (
        <ExerciseModal
          exercise={editing}
          isNew={isNew}
          machines={machines}
          onClose={handleClose}
          onSaved={handleSaved}
        />
      )}

      {showDeleteAll && (
        <DeleteAllModal
          title="Borrar todos los ejercicios"
          description={`Se eliminarán permanentemente los ${exercises.length} ejercicios. Esta acción no se puede deshacer.`}
          onConfirm={handleDeleteAll}
          onClose={() => setShowDeleteAll(false)}
          confirm={deleteConfirm}
          setConfirm={setDeleteConfirm}
          loading={deletingAll}
        />
      )}
    </div>
  );
}

function ExerciseModal({ exercise, isNew, machines, onClose, onSaved }) {
  const [form, setForm] = useState({ ...exercise });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Structured grupo_muscular state
  const _parsed = parseGrupos(exercise.grupo_muscular);
  const [primaryGrupo, setPrimaryGrupo] = useState(_parsed.primary || "");
  const [secondaryGrupos, setSecondaryGrupos] = useState(_parsed.secondary || []);

  // Accessories state
  const [allAccesorios, setAllAccesorios] = useState([]);
  const [selAccesorios, setSelAccesorios] = useState(exercise.accesorios || []);

  useEffect(() => {
    accesoriosApi.adminList().then(setAllAccesorios).catch(() => {});
  }, []);

  const addAcc = (id) => { if (!id || selAccesorios.includes(id)) return; setSelAccesorios(p => [...p, id]); };
  const removeAcc = (id) => setSelAccesorios(p => p.filter(a => a !== id));

  const addSecondary = (val) => {
    if (!val || secondaryGrupos.includes(val) || val === primaryGrupo) return;
    setSecondaryGrupos((prev) => [...prev, val]);
  };
  const removeSecondary = (val) => setSecondaryGrupos((prev) => prev.filter((g) => g !== val));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.nombre?.trim()) { setError("El nombre es obligatorio"); return; }
    setSaving(true); setError("");
    try {
      const allGrupos = [primaryGrupo, ...secondaryGrupos].filter(Boolean);
      const payload = {
        nombre: form.nombre.trim(),
        codigo: form.codigo?.trim() || undefined,
        numero_maquina: form.numero_maquina?.trim() || null,
        grupo_muscular: allGrupos.length ? allGrupos.join(", ") : null,
        tipo_esfuerzo: form.tipo_esfuerzo || null,
        dificultad: form.dificultad !== "" && form.dificultad !== null ? Number(form.dificultad) : null,
        equipo_necesario: form.equipo_necesario || "libre",
        video_url: form.video_url?.trim() || null,
        instrucciones_breves: form.instrucciones_breves?.trim() || null,
        accesorios: selAccesorios,
      };
      if (isNew) { await ejerciciosApi.create(payload); }
      else { await ejerciciosApi.update(form.id, payload); }
      onSaved();
    } catch (e) { setError(formatApiError(e?.response?.data?.detail) || "Error guardando"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="exercise-modal">
      <div className="w-full max-w-4xl bg-[#0d0d0d] border border-white/10 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/8 shrink-0">
          <div>
            <h2 className="font-black text-sm tracking-widest uppercase">{isNew ? "Nuevo ejercicio" : "Editar ejercicio"}</h2>
            {!isNew && <p className="text-[#FFCC00]/60 text-[10px] font-black mt-0.5">{form.codigo}</p>}
          </div>
          <button onClick={onClose} data-testid="close-exercise-modal" className="text-white/30 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 space-y-4 flex-1">
          {/* Nombre */}
          <div>
            <label className={LABEL}>Nombre *</label>
            <input value={form.nombre} onChange={(e) => set("nombre", e.target.value)}
              placeholder="Ej: Fondos en paralelas, Plancha abdominal…" data-testid="ex-nombre"
              className={INPUT} autoFocus />
          </div>

          {/* Equipo + Máquina */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Equipo necesario</label>
              <select value={form.equipo_necesario} onChange={(e) => {
                const v = e.target.value;
                set("equipo_necesario", v);
                if (v !== "maquina") set("numero_maquina", "");
              }} data-testid="ex-equipo" className={INPUT + " appearance-none"}>
                {EQUIPOS.map((eq) => <option key={eq.val} value={eq.val}>{eq.label}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Máquina {form.equipo_necesario !== "maquina" ? "(opcional)" : "*"}</label>
              <select value={form.numero_maquina || ""} onChange={(e) => set("numero_maquina", e.target.value || null)}
                data-testid="ex-maquina" className={INPUT + " appearance-none"}
                disabled={form.equipo_necesario !== "maquina"}>
                <option value="">— Sin máquina —</option>
                {machines.filter((m) => m.activa).map((m) => (
                  <option key={m.numero} value={m.numero}>{m.numero} · {m.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Grupo + Tipo de esfuerzo */}
          <div className="space-y-3">
            <div>
              <label className={LABEL}>Grupo muscular principal</label>
              <select value={primaryGrupo} onChange={(e) => {
                const v = e.target.value;
                setPrimaryGrupo(v);
                // remove from secondary if it was there
                setSecondaryGrupos((prev) => prev.filter((g) => g !== v));
              }} data-testid="ex-grupo-primary" className={INPUT + " appearance-none capitalize"}>
                <option value="">— Sin especificar —</option>
                {GRUPOS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>

              {/* Secondary chips */}
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <select defaultValue="" onChange={(e) => { addSecondary(e.target.value); e.target.value = ""; }}
                    data-testid="ex-grupo-secondary-select"
                    className="flex-1 bg-[#111] border border-white/12 text-white/60 px-3 py-2 text-[11px] outline-none focus:border-[#FFCC00] transition-colors appearance-none">
                    <option value="">+ Añadir músculo secundario</option>
                    {GRUPOS.filter((g) => g !== primaryGrupo && !secondaryGrupos.includes(g)).map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
                {secondaryGrupos.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="ex-grupo-chips">
                    {secondaryGrupos.map((g) => (
                      <span key={g} className="inline-flex items-center gap-1 bg-white/8 border border-white/12 text-white/70 text-[10px] uppercase tracking-wider px-2 py-1">
                        {g}
                        <button type="button" onClick={() => removeSecondary(g)} className="text-white/40 hover:text-white/90 ml-0.5">
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className={LABEL}>Tipo de esfuerzo</label>
              <select value={form.tipo_esfuerzo || ""} onChange={(e) => set("tipo_esfuerzo", e.target.value || null)}
                data-testid="ex-esfuerzo" className={INPUT + " appearance-none"}>
                <option value="">— Sin especificar —</option>
                {ESFUERZOS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Dificultad + Código */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Dificultad</label>
              <select value={form.dificultad ?? ""} onChange={(e) => set("dificultad", e.target.value === "" ? null : Number(e.target.value))}
                data-testid="ex-dificultad" className={INPUT + " appearance-none"}>
                {DIFS.map((d) => <option key={String(d.val)} value={d.val ?? ""}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Código {isNew && <span className="text-white/25 normal-case">(se genera automático)</span>}</label>
              <input value={form.codigo || ""} onChange={(e) => set("codigo", e.target.value)}
                placeholder="Ej: FREE-01, 32-02…" data-testid="ex-codigo"
                className={INPUT} />
            </div>
          </div>

          {/* Vídeo */}
          <div>
            <label className={LABEL}>URL de vídeo tutorial</label>
            <input value={form.video_url || ""} onChange={(e) => set("video_url", e.target.value)}
              placeholder="https://youtube.com/watch?v=…" data-testid="ex-video"
              className={INPUT} />
          </div>

          {/* Instrucciones */}
          <div>
            <label className={LABEL}>Instrucciones breves</label>
            <textarea value={form.instrucciones_breves || ""} onChange={(e) => set("instrucciones_breves", e.target.value)}
              rows={4} placeholder="Trabaja: …&#10;Cómo hacerlo: …&#10;Precauciones: …" data-testid="ex-instrucciones"
              className={INPUT + " resize-none"} />
          </div>

          {/* Accesorios */}
          <div>
            <label className={LABEL}>Accesorios necesarios</label>
            <select defaultValue="" onChange={(e) => { addAcc(e.target.value); e.target.value = ""; }}
              data-testid="ex-accesorio-select"
              className={INPUT + " appearance-none"}>
              <option value="">+ Añadir accesorio</option>
              {allAccesorios.filter(a => a.activo && !selAccesorios.includes(a.id)).map(a => (
                <option key={a.id} value={a.id}>{a.tipo ? `[${a.tipo}] ` : ""}{a.nombre}</option>
              ))}
            </select>
            {selAccesorios.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ex-accesorios-chips">
                {selAccesorios.map(id => {
                  const a = allAccesorios.find(x => x.id === id);
                  return a ? (
                    <span key={id} className="inline-flex items-center gap-1 bg-white/8 border border-white/12 text-white/70 text-[10px] uppercase tracking-wider px-2 py-1">
                      {a.nombre}
                      <button type="button" onClick={() => removeAcc(id)} className="text-white/40 hover:text-white ml-0.5"><X size={10} /></button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
            {allAccesorios.length === 0 && (
              <p className="text-[10px] text-white/25 mt-1">Crea accesorios en Máquinas → Accesorios para poder asociarlos aquí.</p>
            )}
          </div>

          {error && <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-4 py-3" data-testid="ex-error">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-white/8 shrink-0">
          <button onClick={handleSave} disabled={saving} data-testid="save-exercise-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase py-3 hover:bg-white transition-colors disabled:opacity-40">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {saving ? "Guardando…" : isNew ? "Crear ejercicio" : "Guardar cambios"}
          </button>
          <button onClick={onClose} data-testid="cancel-exercise-btn"
            className="px-5 border border-white/12 text-white/40 text-[10px] font-bold uppercase hover:text-white transition-colors">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}


function DeleteAllModal({ title, description, onConfirm, onClose, confirm, setConfirm, loading }) {
  const ready = confirm === "BORRAR";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="delete-all-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-[#0d0d0d] border border-red-500/30 shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-red-500/10 border border-red-500/30 flex items-center justify-center shrink-0">
            <Trash2 size={16} className="text-red-400" />
          </div>
          <h3 className="font-black text-sm tracking-wider text-white uppercase">{title}</h3>
        </div>
        <p className="text-white/50 text-xs leading-relaxed mb-5">{description}</p>
        <div className="mb-5">
          <label className="block text-[10px] uppercase tracking-widest text-white/35 font-bold mb-2">
            Escribe <span className="text-red-400 font-black">BORRAR</span> para confirmar
          </label>
          <input
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="BORRAR" autoFocus data-testid="delete-confirm-input"
            className="w-full bg-black border border-white/12 text-white px-3 py-2.5 text-sm outline-none focus:border-red-500 transition-colors tracking-widest font-bold"
          />
        </div>
        <div className="flex gap-3">
          <button onClick={onConfirm} disabled={!ready || loading} data-testid="confirm-delete-all-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white font-black text-[10px] tracking-[0.2em] uppercase py-3 hover:bg-red-500 transition-colors disabled:opacity-30">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            {loading ? "Borrando..." : "Confirmar borrado"}
          </button>
          <button onClick={onClose} className="px-4 border border-white/12 text-white/40 text-[10px] font-bold uppercase hover:text-white transition-colors">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
